'use-strict';

import 'core-js/features/array/at';

import PCHelpers from './postcodeCheckHelpers';
import VZSpinner from 'Components/VZSpinner';
import { toggleButtonState } from 'Root/util/util';

export default class AddressCheck {
    constructor(availabilityForm, additionSelectElement) {
        this.availabilityForm = availabilityForm;
        this.additionSelectElement = additionSelectElement;
        this.addressCheckEndpoint = this.availabilityForm.dataset?.addressCheckUrl;
        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();
        this.postalCode = null;
        this.houseNumber = null;
        this.addition = null;

        // Hook up a change event to the addition select field so we can grab the PAID to do the availability check using that
        this.additionSelectElement.addEventListener('blur', this.handleAdditionOnBlur.bind(this));
        [...this.availabilityForm.querySelectorAll('input')].forEach((input) => input.addEventListener('blur', this.handleAddressCheckOnBlur.bind(this)));
        this.executeAddressCheckOnLoad();
    }

    /**
     * @function sortAddressExtensions
     * @description Sorts the addresses based on the extension. Manipulates the existing array.
     * @param {Array} addressInfoArray - The array of address information returned from the addressCheck endpoint.
     */
    static sortAddressExtensions(addressInfoArray) {
        addressInfoArray.sort((a, b) => (a.extension.toLowerCase() < b.extension.toLowerCase() ? -1 : 0));

        // Get the index of the no extension array item.
        const indexOfNoExtension = addressInfoArray.indexOf(addressInfoArray.find((address) => !address.extensionRequired));
        if (indexOfNoExtension >= 0) {
            //  Move the no extension item to the beginning of the array, and delete it in the original location, only if it exists.
            addressInfoArray.splice(0, 0, addressInfoArray.splice(indexOfNoExtension, 1)[0]);
        }
    }

    /**
     * @function removeAdditionOptions
     * @description Empties the additionSelectElement except for the default option. Makes the default selected.
     * @param {HTMLOptionsCollection} options - The additionSelectElement options.
     */
    removeAdditionOptions(options) {
        const optionsArray = [...options];
        const defaultOption = optionsArray.find((option) => option.hasAttribute('default'));

        optionsArray.filter((option) => !option.hasAttribute('default')).forEach((option) => option.remove()); // Remove options.
        this.additionSelectElement.selectedIndex = optionsArray.indexOf(defaultOption); // Set the default value as selected.
        this.additionSelectElement.classList.remove('valid'); // Reset the validity state.
    }

    /**
     * @function generateAdditionElementOptions
     * @description Generates the option elements to populate the additionSelectElement based on the address info passed in.
     * @param {Array} addressInfo - The array of address information returned from the addressCheck endpoint.
     * @param {boolean} hasMultipleExtensions - A boolean determining whether the address entered by the user has more extensions than 'No extension'
     */
    generateAdditionElementOptions(addressInfo, hasMultipleExtensions) {
        // skips if there is already additions selected
        if (this.addition) {
            return;
        }
        this.removeAdditionOptions(this.additionSelectElement.options); // Prevent duplicate options from being generated.
        AddressCheck.sortAddressExtensions(addressInfo); // Sort alphabetically based on extensions.

        // Generate option elements based on address info returned from fetch.
        this.additionSelectElement.innerHTML = addressInfo.reduce((result, address, index) => {
            const { paid, extension, extensionRequired, formattedAddress, addressParts } = address;
            const stringifiedAddressParts = JSON.stringify(addressParts).replace(/'/g, '&apos;'); // JSON.stringify does not escape single quotes so it needs to be done manually

            return `${result}
                <option 
                    data-paid="${paid}"
                    value="${extensionRequired ? extension : ''}" 
                    data-formatted-address="${formattedAddress}"
                    data-address-parts="${encodeURI(stringifiedAddressParts)}"
                    ${!hasMultipleExtensions && (index === 0 || extension === this.addition) ? 'selected' : ''}>
                    ${extension}
                </option>
            `;
        }, this.additionSelectElement.innerHTML);
    }

    /**
     * @function handleAdditionOnBlur
     * @description Grabs the PAID to be used for the availability check. Sets the address to be used when the availability service returns
     * 'unableToOrder = true'. Validates the additionSelectElement and enables the CTA.
     * @param {Event} event - The blur event fired from the additionSelectElement.
     */
    handleAdditionOnBlur(event) {
        const { target: additionSelectElement } = event;
        const selectedExtensionOption = [...additionSelectElement.options].find((option) => option.selected);
        const { paid: addressCheckPAID, formattedAddress: addressCheckAddress, addressParts } = selectedExtensionOption.dataset;
        this.addition = selectedExtensionOption.value; // set addition to stop the regeneration of additions dropdown
        const paidHiddenInput = this.availabilityForm.hiddenInputWithPAID;
        paidHiddenInput.value = addressCheckPAID; // Set the PAID to the hidden input so the availability check is performed with the PAID

        // Update the address on change so its up to date for use in fn handleUnableToOrder. This is used to show the address in the availability check response.
        this.availabilityForm.addressCheckAddress = addressCheckAddress;

        // Provide the availability check element with the parts of the address so it can be used when pushing errors to the dataLayer. See fn pushAvailabilityCheckErrorsToDataLayer().
        // This is also used to show addresses in the sidebar for One Net where there is no availability.
        this.availabilityForm.addressCheckAddressParts = JSON.parse(decodeURI(addressParts));

        // If the user has selected a value, mark the field as valid and enable the CTA to perform the availability check.
        if (additionSelectElement.checkValidity()) {
            additionSelectElement.classList.add('valid');

            // Enable the CTA once a selection has been made.
            toggleButtonState(this.availabilityForm.submitButton, true);
        }

        additionSelectElement.setAttribute('value', event.target.value);
    }

    /**
     * @function handleAddressCheckOnBlur
     * @description Executes the address check on blur of the postcode and/or house number field, given that both have a valid value.
     * @param {Event} event - The blur event.
     */
    handleAddressCheckOnBlur() {
        const { postcodeInputElement, houseNumberInputElement } = this.availabilityForm;
        // checks if there was anything changed since previous time
        if (this.postalCode !== postcodeInputElement.value || this.houseNumber !== houseNumberInputElement.value) {
            this.addition = null; // resets additions after a change in postal or houseNumber
            this.postalCode = postcodeInputElement.value;
            this.houseNumber = houseNumberInputElement.value;
            const isMinimumRequirementMet = [postcodeInputElement, houseNumberInputElement].every((element) => element.value && element.checkValidity());
            if (isMinimumRequirementMet) {
                this.executeAddressCheck().catch((error) => PCHelpers.showFeedbackMessage.bind(this.availabilityForm)(error)); // Handle rejection from promise returned in fn executeAddressCheck.
            }
        }
    }

    /**
     * @function handleAddressNotFound
     * @description Handles the scenario where no address is found.
     */
    handleAddressNotFound() {
        this.removeAdditionOptions(this.additionSelectElement.options); // Reset the additionSelectElement when an address is not found.
        toggleButtonState(this.availabilityForm.submitButton, false);
        PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message');
    }

    /**
     * @function handleMultipleAdditions
     * @description When the address check responds with multiple additions for a postcode and house number,
     * this will generate options and append them to the additionSelectElement.
     * @param {Object} addressCheckResponseData - The JSON response returned from the address check.
     */
    handleMultipleAdditions(addressCheckResponseData) {
        const { addressInfo, hasMultipleExtensions } = addressCheckResponseData;
        const { additionHelpText } = this.availabilityForm.dataset;

        if (!this.addition) {
            this.additionSelectElement.classList.remove('valid'); // Reset the validity state of the addition element
        }
        this.generateAdditionElementOptions(addressInfo, hasMultipleExtensions); // Populate the additions field.
        this.additionSelectElement.removeAttribute('read-only'); // In case the field was previously disabled, enable it again.
        PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message');
        PCHelpers.showFeedbackMessage.bind(this.availabilityForm)(additionHelpText, false); // Show a help message when there are multiple extensions
        if (!this.addition) {
            toggleButtonState(this.availabilityForm.submitButton, false); // Disable the CTA until a selection has been made
        }
    }

    /**
     * @function handleSuccessfulAddressCheck
     * @description Handles the successful addressCheck scenario.
     * @param {Object} addressCheckResponseData - The JSON response returned from the address check.
     */
    handleSuccessfulAddressCheck(addressCheckResponseData) {
        const { formattedAddress = null, hasMultipleExtensions, addressInfo } = addressCheckResponseData;

        // Update the address on change so its up to date for use in fn handleUnableToOrder
        this.availabilityForm.addressCheckAddress = formattedAddress;
        this.generateAdditionElementOptions(addressInfo, hasMultipleExtensions); // Populate the additions field.

        // Disable the field when there are no extensions apart from 'No Extension'.
        if (!hasMultipleExtensions && !addressInfo.extensionRequired) {
            this.additionSelectElement.setAttribute('read-only', 'true');
            this.additionSelectElement.dispatchEvent(new Event('blur'));
        }

        PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message, .error-msg');
    }

    /**
     * @function executeAddressCheck
     * @description Executes the address check.
     * @returns {Promise} A promise determining whether the address check was succesful or not. Rejected when address is not found,
     * resolved in other scenarios so the availability check can be performed.
     */
    executeAddressCheck() {
        this.spinner.start();

        /**
         * BO-1570 - To make sure that the availability check is only executed for found addresses, a promise needs to be returned and
         * rejected when the address is not found so that the availability check is not performed.
         */
        return new Promise((resolve, reject) => {
            PCHelpers.fetchAddressAndAvailability(this.addressCheckEndpoint, this.availabilityForm)
                .then((addressCheckResponseData) => {
                    const multipleExtensionsFound = addressCheckResponseData?.multipleExtensionsFound;

                    if (!addressCheckResponseData?.success && multipleExtensionsFound) {
                        this.handleMultipleAdditions(addressCheckResponseData);
                        resolve();
                    } else if (!addressCheckResponseData?.success) {
                        this.handleAddressNotFound();
                        reject(addressCheckResponseData?.serverErrors[0]);
                    } else {
                        this.handleSuccessfulAddressCheck(addressCheckResponseData);
                        resolve();
                    }
                })
                .catch(PCHelpers.handleErrors.bind(this))
                .finally(this.spinner.stop);
        });
    }

    /**
     * @function executeAddressCheckOnLoad
     * @description Executes the address check on load if values are present. This is mainly so the extension list is populated already,
     * and that the CTA is enabled/disabled based on the status of the address check.
     */
    executeAddressCheckOnLoad() {
        const { postcodeInputElement, houseNumberInputElement } = this.availabilityForm;

        if (postcodeInputElement.checkValidity() && houseNumberInputElement.checkValidity()) {
            this.executeAddressCheck().catch((error) => PCHelpers.showFeedbackMessage.bind(this.availabilityForm)(error)); // Handle rejection from promise returned in fn executeAddressCheck.
        }
    }
}
